// Immersive Quest app (com.gathrxr.quest) pieces that live inside <XR>.
//
//  - <QuestImmersiveBridge/>: hands the session requested on load
//    (utils/questImmersiveSession) to three.js once the renderer exists.
//  - <QuestVRAccountPanel/>: the app has no 2D page, so signing in happens in
//    VR. "Sign in" opens a code screen first (TV-style, services/deviceLink):
//    the headset shows a code + gathrxr.com/l/CODE, the person approves it on
//    their phone or computer (/link, any sign-in method, new accounts too) and
//    the headset signs itself in. "Sign in with email instead" is the old
//    email + password form on the VR keyboard (signInWithPassword).
//    Opens once on the first launch while signed out (Sign in / Continue as
//    guest), and from the menu's "Leave world" button, which in the app shows
//    Sign in or Sign out plus Close app.
//
// Outside the immersive app the bridge does nothing; the panel only opens
// there as the code screen (VR Home's account chip in the Quest Browser).
import React, { useEffect, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { useXR } from '@react-three/xr';
import { create } from 'zustand';
import { supabase } from '../../config/supabase';
import { isQuestImmersiveApp } from '../../utils/questApp';
import { attachQuestRenderer, quitQuestApp } from '../../utils/questImmersiveSession';
import { isEmailNotConfirmed } from '../../services/onboarding';
import { finishDeviceSignIn, pollDeviceLink, startDeviceLink, type DeviceLinkStart } from '../../services/deviceLink';
import { LINK_PAGE_LABEL, formatCode, formatCountdown, shortLinkLabel } from '../../utils/deviceLinkCode';
import { vrColors as colors, noRaycast, Surface } from '../VR/vrUi';
import VRKeyboard, { KEYBOARD_HEIGHT, type KeyAction } from '../VR/VRKeyboard';
import { FloatingPanelMount } from '../games/kit/vrKit';


/** Inside <XR>: give the early session to the renderer. */
export function QuestImmersiveBridge() {
  const gl = useThree((s) => s.gl);
  useEffect(() => {
    if (!isQuestImmersiveApp()) return;
    attachQuestRenderer(gl as any);
    return () => attachQuestRenderer(null);
  }, [gl]);
  return null;
}

// ---- account panel ----------------------------------------------------------

type View = 'home' | 'code' | 'signin' | 'done';
/** `done`: finishing the code sign-in with the approved one-time token. */
const useQuestPanel = create<{ open: boolean; view: View; tokenHash?: string; linkedEmail?: string | null }>(() => ({ open: false, view: 'home' }));
/** "Leave world" in the immersive app opens this instead of the 2D dashboard. */
export const openQuestAccountPanel = (view: Exclude<View, 'done'> = 'home') => useQuestPanel.setState({ open: true, view });
const closePanel = () => useQuestPanel.setState({ open: false, view: 'home' });

const WELCOMED_KEY = 'gx-quest-welcomed';
const clip = (s: string, n: number) => (s.length > n ? `…${s.slice(-(n - 1))}` : s);

function Btn({ label, x, y, w, h = .07, fontSize = .021, tone = 'card', disabled = false, name, onPress }: {
  label: string; x: number; y: number; w: number; h?: number; fontSize?: number; tone?: 'card' | 'teal' | 'red'; disabled?: boolean; name?: string; onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const base = tone === 'teal' ? colors.teal : tone === 'red' ? '#e5484d' : colors.card;
  const bg = disabled ? '#1a2633' : hover ? (tone === 'card' ? colors.hover : tone === 'teal' ? '#2ee6d6' : '#f0676b') : base;
  const fg = disabled ? colors.muted : tone === 'teal' ? '#04211e' : colors.text;
  return <group position={[x, y, .006]} name={name}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}>
    <Surface width={w} height={h} color={bg} r={.014} />
    <Text raycast={noRaycast} position={[0, 0, .003]} fontSize={fontSize} maxWidth={w - .016} textAlign="center" color={fg}>{label}</Text>
  </group>;
}

function Field({ label, value, placeholder, active, y, w, onPress }: { label: string; value: string; placeholder: string; active: boolean; y: number; w: number; onPress: () => void }) {
  return <group position={[0, y, .006]} onClick={(e) => { e.stopPropagation(); onPress(); }}>
    <Text raycast={noRaycast} position={[-w / 2, .05, 0]} anchorX="left" fontSize={.015} color={colors.muted}>{label}</Text>
    <Surface width={w + .008} height={.068} color={active ? colors.teal : colors.border} z={-.002} r={.014} />
    <Surface width={w} height={.06} color={colors.card} r={.012} />
    <Text raycast={noRaycast} position={[-w / 2 + .016, 0, .004]} anchorX="left" fontSize={.021} maxWidth={w - .03}
      color={value ? colors.text : colors.muted}>{value ? `${value}${active ? '|' : ''}` : placeholder}</Text>
  </group>;
}

const W = .74;

function SignInForm({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [field, setField] = useState<'email' | 'password'>('email');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const submit = async () => {
    if (busy) return;
    if (!email.includes('@')) { setField('email'); setMsg('Type the email address you use for GathrXR.'); return; }
    if (!password) { setField('password'); setMsg('Type your password.'); return; }
    setBusy(true); setMsg('Signing in…');
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) {
      setMsg(isEmailNotConfirmed(error) ? 'Confirm your email first (check your inbox), then try again.'
        : /invalid/i.test(error.message) ? 'That email and password don\'t match.' : 'Could not sign in. Try again.');
      return;
    }
    setMsg(null);
    onDone();
  };
  const onKey = (a: KeyAction) => {
    const set = field === 'email' ? setEmail : setPassword;
    if (a.type === 'text') set((v) => (v + (field === 'email' ? a.value.replace(/\s/g, '') : a.value)).slice(0, 120));
    else if (a.type === 'backspace') set((v) => v.slice(0, -1));
    else if (field === 'email') setField('password');
    else void submit();
  };

  const H = .43;
  const top = H / 2;
  const fullH = H + KEYBOARD_HEIGHT + .05;
  return <group position={[0, .22, 0]} onClick={(e) => e.stopPropagation()}>
    <group position={[0, top - fullH / 2, 0]}>
      <Surface width={W + .008} height={fullH + .008} color={colors.border} z={-.003} r={.022} />
      <Surface width={W} height={fullH} color={colors.panel} r={.02} />
    </group>
    <Text raycast={noRaycast} position={[-W / 2 + .025, top - .035, .006]} anchorX="left" fontSize={.027} color={colors.text}>Sign in to GathrXR</Text>
    <Btn name="quest-signin-back" label="Back" x={W / 2 - .07} y={top - .04} w={.11} h={.05} fontSize={.016} onPress={onBack} />
    <Field label="Email" value={email} placeholder="you@example.com" active={field === 'email'} y={top - .125} w={W - .05} onPress={() => setField('email')} />
    <Field label="Password" value={'•'.repeat(password.length)} placeholder="Your password" active={field === 'password'} y={top - .225} w={W - .05} onPress={() => setField('password')} />
    <Text raycast={noRaycast} position={[-W / 2 + .025, top - .29, .006]} anchorX="left" anchorY="top" fontSize={.0145} maxWidth={W - .05}
      color={msg && msg !== 'Signing in…' ? '#ffb1ad' : colors.muted}>
      {msg || 'No account yet? Create one at gathrxr.com on your phone or computer, then sign in here.'}
    </Text>
    <Btn name="quest-signin-submit" label={busy ? 'Signing in…' : 'Sign in'} x={0} y={top - .375} w={W - .05} tone="teal" disabled={busy} onPress={() => void submit()} />
    <group position={[0, top - H - .02 - KEYBOARD_HEIGHT / 2 + .031, .006]}>
      <VRKeyboard mode="url" goLabel={field === 'email' ? 'Next' : 'Sign in'} onKey={onKey} />
    </group>
  </group>;
}

const POLL_MS = 2000;
const CODE_TTL_MS = 10 * 60_000;

/**
 * TV-style sign-in: a code and a short link to open on a phone / computer.
 * Polls only while mounted (the panel is open on this screen); a new code
 * replaces the old one when it runs out.
 */
function CodeSignIn({ onEmail, onBack, backLabel }: { onEmail: () => void; onBack: () => void; backLabel: string }) {
  const [link, setLink] = useState<(DeviceLinkStart & { expires: number }) | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [round, setRound] = useState(0); // bump = fetch a fresh code

  // Get a code, then poll it every ~2 s until it's approved, expires or we close.
  useEffect(() => {
    let alive = true;
    let timer = 0;
    const later = (fn: () => void, ms: number) => { timer = window.setTimeout(() => { if (alive) fn(); }, ms); };
    const again = () => { if (alive) setRound((r) => r + 1); };
    setLink(null);
    void startDeviceLink().then((r) => {
      if (!alive) return;
      if ('error' in r) { setMsg(r.error); later(again, 10_000); return; }
      setMsg(null);
      // Count down on this headset's clock (it may disagree with the server's).
      const ttl = Date.parse(r.expiresAt) - Date.now();
      const expires = Date.now() + (ttl > 0 && ttl <= CODE_TTL_MS ? ttl : CODE_TTL_MS - 10_000);
      setLink({ ...r, expires });
      const tick = async () => {
        if (Date.now() >= expires) { again(); return; }
        const p = await pollDeviceLink(r.code, r.secret);
        if (!alive) return;
        if (p.status === 'pending') { setMsg(null); later(tick, POLL_MS); return; }
        if (p.status === 'error') { setMsg(p.message); later(tick, POLL_MS * 2); return; }
        if (p.status !== 'approved') { again(); return; }
        useQuestPanel.setState({ view: 'done', tokenHash: p.tokenHash, linkedEmail: p.email });
      };
      later(() => void tick(), POLL_MS);
    });
    return () => { alive = false; window.clearTimeout(timer); };
  }, [round]);

  // Countdown, once a second.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const H = .5;
  const top = H / 2;
  const x0 = -W / 2 + .025;
  const left = link ? link.expires - now : 0;
  return <group onClick={(e) => e.stopPropagation()} name="quest-code-signin">
    <Surface width={W + .008} height={H + .008} color={colors.border} z={-.003} r={.022} />
    <Surface width={W} height={H} color={colors.panel} r={.02} />
    <Text raycast={noRaycast} position={[x0, top - .035, .006]} anchorX="left" fontSize={.027} color={colors.text}>Sign in to GathrXR</Text>
    <Btn name="quest-code-back" label={backLabel} x={W / 2 - .07} y={top - .04} w={.11} h={.05} fontSize={.016} onPress={onBack} />
    <Text raycast={noRaycast} position={[x0, top - .09, .006]} anchorX="left" fontSize={.017} maxWidth={W - .05} color={colors.muted}>
      On your phone or computer, go to
    </Text>
    <Text raycast={noRaycast} position={[0, top - .135, .006]} fontSize={.034} color={colors.teal} name="quest-code-link">
      {link ? shortLinkLabel(link.code) : ' '}
    </Text>
    <Text raycast={noRaycast} position={[0, top - .18, .006]} fontSize={.0155} maxWidth={W - .05} textAlign="center" color={colors.muted}>
      {`and sign in. Or go to ${LINK_PAGE_LABEL} and enter this code:`}
    </Text>
    <group position={[0, top - .262, .004]}>
      <Surface width={.4} height={.11} color={colors.card} r={.018} />
      <Text raycast={noRaycast} position={[0, 0, .004]} fontSize={.068} letterSpacing={.12} color={colors.text} name="quest-code-value">
        {link ? formatCode(link.code) : '•••-•••'}
      </Text>
    </group>
    <Text raycast={noRaycast} position={[0, top - .345, .006]} fontSize={.015} maxWidth={W - .05} textAlign="center"
      color={msg ? '#ffb1ad' : colors.muted}>
      {msg || (link ? `Waiting for your phone…   New code in ${formatCountdown(left)}` : 'Getting a code…')}
    </Text>
    <Btn name="quest-code-email" label="Sign in with email instead" x={0} y={top - .425} w={W - .05} onPress={onEmail} />
  </group>;
}

// A token is completed once, even if the effect runs twice (React dev).
const finished = new Set<string>();

/** The code was approved on the phone: sign in with the one-time token, say so, close. */
function CodeDone() {
  const { tokenHash, linkedEmail } = useQuestPanel();
  const [state, setState] = useState<'busy' | 'ok' | 'failed'>('busy');
  useEffect(() => {
    if (!tokenHash || finished.has(tokenHash)) return;
    finished.add(tokenHash);
    let t = 0;
    void finishDeviceSignIn(tokenHash).then((err) => {
      setState(err ? 'failed' : 'ok');
      t = window.setTimeout(() => (err ? useQuestPanel.setState({ view: 'code', tokenHash: undefined }) : closePanel()), err ? 2600 : 1800);
    });
    return () => window.clearTimeout(t);
  }, [tokenHash]);
  const H = .26;
  return <group onClick={(e) => e.stopPropagation()} name="quest-code-done">
    <Surface width={W + .008} height={H + .008} color={colors.border} z={-.003} r={.022} />
    <Surface width={W} height={H} color={colors.panel} r={.02} />
    <Text raycast={noRaycast} position={[0, .03, .006]} fontSize={.048} color={state === 'failed' ? '#ffb1ad' : colors.teal}>
      {state === 'ok' ? 'You\'re signed in' : state === 'failed' ? 'That didn\'t work' : 'Signing in…'}
    </Text>
    <Text raycast={noRaycast} position={[0, -.04, .006]} fontSize={.019} maxWidth={W - .05} textAlign="center" color={colors.muted}>
      {state === 'failed' ? 'Getting you a new code…' : linkedEmail ? clip(linkedEmail, 44) : ' '}
    </Text>
  </group>;
}

function Home({ email, onSignIn }: { email: string | null; onSignIn: () => void }) {
  const H = .34;
  const top = H / 2;
  const signOut = async () => { await supabase.auth.signOut().catch(() => {}); };
  return <group onClick={(e) => e.stopPropagation()}>
    <Surface width={W + .008} height={H + .008} color={colors.border} z={-.003} r={.022} />
    <Surface width={W} height={H} color={colors.panel} r={.02} />
    <Text raycast={noRaycast} position={[-W / 2 + .025, top - .035, .006]} anchorX="left" fontSize={.027} color={colors.text}>
      {email ? 'GathrXR' : 'Welcome to GathrXR'}
    </Text>
    <Text raycast={noRaycast} position={[-W / 2 + .025, top - .072, .006]} anchorX="left" fontSize={.015} maxWidth={W - .05} color={colors.muted}>
      {email ? `Signed in as ${clip(email, 40)}` : 'Sign in for voice, your avatar and your worlds, or look around as a guest.'}
    </Text>
    {email
      ? <Btn name="quest-signout" label="Sign out" x={-W / 4 + .006} y={top - .15} w={W / 2 - .037} onPress={() => void signOut()} />
      : <Btn name="quest-signin" label="Sign in" x={-W / 4 + .006} y={top - .15} w={W / 2 - .037} tone="teal" onPress={onSignIn} />}
    <Btn name="quest-guest" label={email ? 'Back to the world' : 'Continue as guest'} x={W / 4 - .006} y={top - .15} w={W / 2 - .037} onPress={closePanel} />
    <Btn name="quest-quit" label="Close app" x={0} y={top - .26} w={W - .05} tone="red" onPress={quitQuestApp} />
  </group>;
}

/**
 * Inside <XR>: the immersive app's sign-in / account panel (VR only). In a
 * regular browser it only shows as the code / email sign-in, when something
 * (VR Home's account chip) opens it there.
 */
export function QuestVRAccountPanel() {
  const questApp = isQuestImmersiveApp();
  const presenting = useXR((s) => !!s.session);
  const { open, view } = useQuestPanel();
  const [email, setEmail] = useState<string | null>(null);
  const [known, setKnown] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => { setEmail(session?.user?.email ?? null); setKnown(true); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => setEmail(session?.user?.email ?? null));
    return () => subscription.unsubscribe();
  }, []);

  // First time in VR this app session, signed out: offer sign-in once.
  useEffect(() => {
    if (!questApp || !presenting || !known || email) return;
    try { if (sessionStorage.getItem(WELCOMED_KEY)) return; sessionStorage.setItem(WELCOMED_KEY, '1'); } catch { /* private mode */ }
    openQuestAccountPanel('home');
  }, [questApp, presenting, known, email]);

  // Leaving VR closes it (the browser has no Home screen to fall back to).
  useEffect(() => { if (!presenting && !questApp) closePanel(); }, [presenting, questApp]);

  if (!presenting || !open) return null;
  if (!questApp && (view === 'home' || (email && view !== 'done'))) return null;
  const toHome = () => (questApp ? useQuestPanel.setState({ view: 'home' }) : closePanel());
  return <FloatingPanelMount distance={0.6} drop={0.35}>
    {view === 'done'
      ? <CodeDone />
      : view === 'code' && !email
      ? <CodeSignIn onEmail={() => useQuestPanel.setState({ view: 'signin' })} onBack={toHome} backLabel={questApp ? 'Back' : 'Close'} />
      : view === 'signin' && !email
        ? <SignInForm onDone={closePanel} onBack={() => useQuestPanel.setState({ view: 'code' })} />
        : <Home email={email} onSignIn={() => useQuestPanel.setState({ view: 'code' })} />}
  </FloatingPanelMount>;
}
